"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { FileUp, FolderClosed, Loader2, X } from "lucide-react";

import { SkillConfirmDialog } from "@/components/command-skills/skill-confirm-dialog";
import {
  fileNameFromUpload,
  formatBytes,
  skillFolderChoices,
  skillReadLine,
  STANDARD_SKILL_FOLDERS,
  validateSkillFilePath,
} from "@/components/command-skills/skill-utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { SkillFile } from "@/types/command-skill";

const CUSTOM_FOLDER = "__custom__";

interface AddFileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "add" | "replace";
  fixedPath?: string;
  path?: string;
  existing?: SkillFile[];
  pending: boolean;
  error: string | null;
  onSubmit: (path: string, file: File, onProgress: (percent: number) => void) => void;
}

function cleanFolder(value: string) {
  return value.trim().replace(/^\/+|\/+$/g, "");
}

function folderNameError(value: string) {
  if (!value) return "Name the folder";
  if (value.includes("/") || value.includes("\\") || value.includes("..")) {
    return "Use one folder name, like docs";
  }
  if (value === "." || value === "SKILL.md") return "Choose a different folder name";
  return null;
}

export function AddFileDialog({
  open,
  onOpenChange,
  mode,
  fixedPath,
  path: altPath,
  existing = [],
  pending,
  error,
  onSubmit,
}: AddFileDialogProps) {
  const effectiveFixedPath = fixedPath ?? altPath;
  const folders = useMemo(() => skillFolderChoices(existing), [existing]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [folder, setFolder] = useState("references");
  const [customFolder, setCustomFolder] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      setFolder("references");
      setCustomFolder("");
      setFile(null);
      setDragging(false);
      setLocalError(null);
      setReplaceOpen(false);
      setProgress(null);
    }
  }, [open]);

  const custom = folder === CUSTOM_FOLDER;
  const folderName = custom ? cleanFolder(customFolder) : folder;
  const fileName = file ? fileNameFromUpload(file.name) : "";
  const path = useMemo(() => {
    if (mode === "replace" && effectiveFixedPath) return effectiveFixedPath;
    if (!fileName || !folderName) return "";
    return `${folderName}/${fileName}`;
  }, [effectiveFixedPath, fileName, folderName, mode]);

  const chooseFile = (next: File | null) => {
    setFile(next);
    setLocalError(null);
  };

  const requestSubmit = () => {
    if (!file) {
      setLocalError("Choose a file");
      return;
    }
    if (mode === "add" && custom) {
      const folderError = folderNameError(folderName);
      if (folderError) {
        setLocalError(folderError);
        return;
      }
    }
    const pathError = validateSkillFilePath(path, existing);
    if (pathError) {
      setLocalError(pathError);
      return;
    }
    if (mode === "add" && existing.some((item) => item.path === path)) {
      setReplaceOpen(true);
      return;
    }
    setProgress(0);
    onSubmit(path, file, setProgress);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
        <DialogContent
          className="gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-lg sm:rounded-2xl"
          onPointerDownOutside={(event) => pending && event.preventDefault()}
          onEscapeKeyDown={(event) => pending && event.preventDefault()}
        >
          <DialogHeader className="space-y-1 border-b border-border/70 px-6 py-5 text-left">
            <DialogTitle className="text-base font-bold tracking-tight">
              {mode === "replace" ? "Replace file" : "Add file"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {mode === "replace"
                ? "The new file keeps the same place in the skill."
                : "Drop a file into a folder. Its name is taken from the file."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-6 py-5">
            {mode === "add" ? (
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Folder</Label>
                <Select
                  value={folder}
                  onValueChange={(value) => {
                    setFolder(value);
                    setLocalError(null);
                  }}
                >
                  <SelectTrigger className="h-10 rounded-xl text-sm">
                    <div className="flex items-center gap-2">
                      <FolderClosed className="size-3.5 text-muted-foreground" />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {folders.map((choice) => (
                      <SelectItem key={choice} value={choice} className="text-sm">
                        {choice}
                      </SelectItem>
                    ))}
                    <SelectItem value={CUSTOM_FOLDER} className="text-sm">
                      Custom folder
                    </SelectItem>
                  </SelectContent>
                </Select>
                {custom ? (
                  <div className="space-y-1.5">
                    <Input
                      value={customFolder}
                      disabled={pending}
                      placeholder="docs"
                      autoFocus
                      onChange={(event) => {
                        setCustomFolder(event.target.value);
                        setLocalError(null);
                      }}
                      className="h-10 rounded-xl text-sm"
                    />
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                      Creates a new folder next to {STANDARD_SKILL_FOLDERS.join(", ")}. Those names are usual, not required.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="rounded-xl bg-muted/50 px-3 py-2 font-mono text-xs text-muted-foreground">
                {effectiveFixedPath}
              </p>
            )}

            <div className="space-y-2">
              <Label className="text-xs font-semibold">File</Label>
              <div
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  chooseFile(event.dataTransfer.files?.[0] ?? null);
                }}
                className={cn(
                  "flex w-full items-center gap-3 rounded-2xl border border-dashed px-4 py-4 transition-colors",
                  dragging ? "border-primary bg-primary/5" : "border-border bg-muted/20",
                )}
              >
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => inputRef.current?.click()}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-background text-primary shadow-sm">
                    <FileUp className="size-4" />
                  </span>
                  {file ? (
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">{file.name}</span>
                      <span className="block text-[11px] text-muted-foreground">{formatBytes(file.size)}</span>
                    </span>
                  ) : (
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">Drop a file here</span>
                      <span className="block text-[11px] text-muted-foreground">or click to browse</span>
                    </span>
                  )}
                </button>
                {file ? (
                  <button
                    type="button"
                    className="rounded-full p-1 text-muted-foreground hover:bg-background hover:text-foreground"
                    aria-label="Remove file"
                    onClick={() => {
                      chooseFile(null);
                      if (inputRef.current) inputRef.current.value = "";
                    }}
                  >
                    <X className="size-3.5" />
                  </button>
                ) : null}
              </div>
              <input
                ref={inputRef}
                type="file"
                className="sr-only"
                disabled={pending}
                onChange={(event) => chooseFile(event.target.files?.[0] ?? null)}
              />
            </div>

            {path ? (
              <div className="space-y-1 rounded-xl bg-muted/40 px-3 py-2.5">
                <p className="truncate font-mono text-[11px] text-muted-foreground">
                  Saves as <span className="text-foreground">{path}</span>
                </p>
                {fileName && folderName && !(STANDARD_SKILL_FOLDERS as readonly string[]).includes(folderName.split("/")[0]) ? (
                  <p className="text-[11px] leading-relaxed text-muted-foreground">
                    SKILL.md will include <span className="font-mono text-foreground">{skillReadLine(path)}</span> so the agent knows to open it.
                  </p>
                ) : null}
              </div>
            ) : null}

            {localError || error ? (
              <p className="text-xs font-medium text-destructive">{localError || error}</p>
            ) : null}
            {pending && progress !== null ? (
              <p className="text-xs text-muted-foreground">Uploading… {progress}%</p>
            ) : null}
          </div>

          <DialogFooter className="border-t border-border/70 px-6 py-4 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-full px-4 text-xs font-semibold"
              disabled={pending}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="h-9 rounded-full px-5 text-xs font-semibold"
              disabled={pending || !file}
              onClick={requestSubmit}
            >
              {pending ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : null}
              {mode === "replace" ? "Replace" : "Add file"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SkillConfirmDialog
        open={replaceOpen}
        onOpenChange={setReplaceOpen}
        title="Replace file?"
        description={`Replace ${path}?`}
        confirmLabel="Replace"
        pending={pending}
        onConfirm={() => {
          if (!file) return;
          setReplaceOpen(false);
          setProgress(0);
          onSubmit(path, file, setProgress);
        }}
      />
    </>
  );
}
